import {
  Alert,
  Box,
  Button,
  Collapse,
  Grid,
  LinearProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { NotificationPreferences } from '../User/Childs/NotificationPreferences';
import { UserGroupDelete } from './Childs/UserGroupDelete';
import { UserHasGroupRow } from './Childs/UserHasGroupRow';
import {
  IUserGroup,
  IUserGroupFullObject,
  isSystemUserGroup,
} from '../../../Interfaces/IUserGroup';
import { IUser } from '../../../Interfaces/IUser';
import { uploadFetch, useFetch } from '../../../hooks/useFetch';
import { IUserHasUserGroup } from '../../../Interfaces/IUserHasUserGroup';
import { ITopic } from '../../../Interfaces/ITopic';
import { LoadingSpinner } from '../../../core/LoadingSpinner';
import { CustomeTextField } from '../../../generic/CustomeTextField';
import { CustomEditor } from '../../../core/CustomEditor';
import { a11yProps } from '../../../services/dialog.service';
import CustomTabPanel from '../../../core/CustomTabPanel';
import { ActivityLog } from '../../../generic/ActivityLog';

/** Bereiche des Gruppen-Protokolls. */
const GROUP_LOG_AREAS = [
  { label: 'Gruppe', entityTypes: ['UserGroup'] },
  { label: 'Mitglieder', entityTypes: ['User', 'User_has_UserGroup'] },
];

interface IProps {
  idUserGroup: number;
  setIdUserGroup: Function;
  userArray: IUser[];
  userGroupArray: IUserGroup[];
  setUserGroupArray: Function;
}

const getEmptyUserGroup = () => {
  return {
    idUserGroup: -1,
    UserGroup: '',
    Email: null,
    PhoneNumber: null,
    NotifcationOptionBitmask: undefined,
    isSystemGroup: false,
    SystemKey: null,
    Signature: '',
    UserHasUserGroupArray: [],
  } as IUserGroupFullObject;
};

export const UserGroupEdit: React.FC<IProps> = ({
  idUserGroup,
  setIdUserGroup,
  userArray,
  userGroupArray,
  setUserGroupArray,
}) => {
  const [userGroupFullObject, setUserGroupFullObject, wasSuccessfullyLoad] =
    useFetch<IUserGroupFullObject>('/usergroup/fullObject/', idUserGroup);
  const [userGroupObject, setUserGroupObject] = useState(getEmptyUserGroup());
  const [userHasGroupArray, setUserHasGroupArray] = useState<
    IUserHasUserGroup[]
  >([]);
  const [topics, setTopics, wasSuccessfullyLoadTopics] =
    useFetch<ITopic[]>('/topic');
  const [notificationPreferences, setNotificationPreferences] = useState<{
    [key: number]: boolean;
  }>({});

  //
  const [isLoading, setIsLoading] = useState(false);
  const [wasSuccessfullySaved, setWasSuccessfullySaved] = useState(true);
  //
  const [value, setValue] = useState(0);

  /**
   * Systemgruppen stellt das System bereit. Mitglieder und Erreichbarkeit
   * lassen sich pflegen — Bezeichnung, Zweck und der technische Schlüssel
   * nicht, sonst würden die daran hängenden Funktionen ins Leere laufen.
   */
  const isSystemGroup = isSystemUserGroup(userGroupObject);

  //Initialisiere den Userchange, indem die dem Nutzer zugeordneten Vorgänge geladen werden

  const calcNotifcationOption = useCallback((options: number[]): number => {
    let abonnements = 0;
    for (const option of options) {
      abonnements |= 1 << (option - 1);
    }
    return abonnements;
  }, []);

  const calcNotifcationOptionArray = useCallback(
    (notificationBitNumber: number): number[] => {
      const optionen: number[] = [];
      let bitPosition = 0;

      while (notificationBitNumber !== 0) {
        if ((notificationBitNumber & 1) !== 0) {
          optionen.push(bitPosition + 1);
        }
        notificationBitNumber >>= 1;
        bitPosition++;
      }

      return optionen;
    },
    []
  );

  useEffect(() => {
    if (wasSuccessfullyLoadTopics && userGroupFullObject !== undefined) {
      if (
        userGroupFullObject.NotifcationOptionBitmask !== null &&
        userGroupFullObject.NotifcationOptionBitmask !== undefined
      ) {
        setNotificationPreferences(
          calcNotifcationOptionArray(
            userGroupFullObject.NotifcationOptionBitmask
          ).reduce(
            (acc, cur) => {
              acc[cur] = true;
              return acc;
            },
            {} as { [key: number]: boolean }
          )
        );
      }
    }
  }, [
    wasSuccessfullyLoadTopics,
    userGroupFullObject,
    calcNotifcationOptionArray,
  ]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (userGroupFullObject !== undefined) {
      setUserGroupObject(userGroupFullObject);
      setUserHasGroupArray(userGroupFullObject.UserHasUserGroupArray);
    }
  }, [userGroupFullObject]);

  const handleAfterSave = (res: IUserGroupFullObject) => {
    const foundObject = userGroupArray.find(
      x => x.idUserGroup === res.idUserGroup
    );

    if (foundObject === undefined) {
      setUserGroupArray([...userGroupArray, res]);
    } else {
      setUserGroupArray([
        ...userGroupArray.map(x =>
          x.idUserGroup === res.idUserGroup ? res : x
        ),
      ]);
    }
    setIdUserGroup(null);
  };

  const handleSave = () => {
    const uploadObject: IUserGroupFullObject = {
      ...userGroupObject,
      // Das Backend liefert die Spalte als TINYINT (`1`/`0`) aus, die
      // Schnittstelle erwartet aber ein echtes Boolean — ungewandelt weist sie
      // das Speichern mit 400 ab. Gesetzt wird der Wert ohnehin serverseitig.
      isSystemGroup: isSystemGroup,
      UserHasUserGroupArray: userHasGroupArray,
    };
    if (!isSystemGroup && notificationPreferences !== undefined) {
      const notificationBitmask = calcNotifcationOption(
        Object.keys(notificationPreferences)
          .map(x => Number(x))
          .filter(x => notificationPreferences[x])
      );

      if (notificationBitmask !== userGroupObject.NotifcationOptionBitmask) {
        uploadObject.NotifcationOptionBitmask = notificationBitmask;
      } else {
        //remove from uploadObject
        uploadObject.NotifcationOptionBitmask = undefined;
      }
    }
    uploadFetch(
      '/usergroup/fullObject',
      idUserGroup < 0,
      uploadObject,
      handleAfterSave,
      setWasSuccessfullySaved,
      () => {},
      setIsLoading
    );
  };

  if ((idUserGroup > 0 && !wasSuccessfullyLoad) || !wasSuccessfullyLoadTopics) {
    return <Alert severity='error'>Fehler!</Alert>;
  } else if (
    (idUserGroup > 0 && userGroupFullObject === undefined) ||
    topics === undefined
  ) {
    return <LoadingSpinner height='50vh' />;
  } else {
    return (
      <Box>
        <Typography>
          Benutzergruppe
          {userGroupFullObject !== undefined && !isSystemGroup && (
            <UserGroupDelete
              idUserGroup={idUserGroup}
              setIdUserGroup={setIdUserGroup}
              userGroupFullObject={userGroupFullObject}
              userGroupArray={userGroupArray}
              setUserGroupArray={setUserGroupArray}
            />
          )}
        </Typography>
        <Collapse in={isLoading}>
          <LinearProgress />
        </Collapse>

        <Collapse in={!wasSuccessfullySaved}>
          <Alert severity='error'>
            Die Benutzergruppe konnte nicht gespeichert werden!
          </Alert>
        </Collapse>

        <Collapse in={isSystemGroup}>
          <Alert severity='info' sx={{ mt: 2 }}>
            Diese Benutzergruppe wird vom System bereitgestellt. Ihre Mitglieder
            und die Erreichbarkeit können Sie wie gewohnt pflegen; Bezeichnung
            und Zweck sind fest vorgegeben, und die Gruppe lässt sich weder
            löschen noch als normale Benutzergruppe zuordnen.
          </Alert>
        </Collapse>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item sm={12}>
            <CustomeTextField
              label='Gruppenbezeichnung'
              type='string'
              attr='UserGroup'
              object={userGroupObject}
              setObject={setUserGroupObject}
              required
              disabled={isSystemGroup}
            />
          </Grid>

          <Grid item sm={6}>
            <CustomeTextField
              label='E-Mail'
              type='string'
              attr='Email'
              object={userGroupObject}
              setObject={setUserGroupObject}
              null
            />
          </Grid>
          <Grid item sm={6}>
            <CustomeTextField
              label='Telefonnummer'
              type='string'
              attr='PhoneNumber'
              object={userGroupObject}
              setObject={setUserGroupObject}
              null
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 5, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label='basic tabs example'
          >
            <Tab label='Mitglieder' {...a11yProps(0)} value={0} />
            {/* Benachrichtigungen einer Systemgruppe ergeben sich aus ihrer
                Funktion und sind nicht frei wählbar — der Reiter entfällt.
                Die value-Angaben halten die Reiter-Indizes fest, damit das
                Ausblenden den Signatur-Reiter nicht verschiebt. */}
            {!isSystemGroup && (
              <Tab label='Benachrichtigungen' {...a11yProps(1)} value={1} />
            )}
            <Tab label='Signatur' {...a11yProps(2)} value={2} />
            {idUserGroup > 0 && <Tab label='Log' {...a11yProps(3)} value={3} />}
          </Tabs>
        </Box>

        <CustomTabPanel value={value} index={0}>
          <Typography variant='h5'>Mitglieder</Typography>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Benutzer</TableCell>
                <TableCell>Vor- und Nachname</TableCell>
                <TableCell sx={{ width: 100 }}>Mitglied</TableCell>
                <TableCell sx={{ width: 100 }}>Gruppenadmin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userArray.map(x => (
                <UserHasGroupRow
                  key={`idUser-${x.idUser}`}
                  userObject={x}
                  userHasUserGroup={userHasGroupArray}
                  setUserHasUserGroup={setUserHasGroupArray}
                />
              ))}
            </TableBody>
          </Table>
        </CustomTabPanel>

        <CustomTabPanel value={value} index={isSystemGroup ? -1 : 1}>
          <NotificationPreferences
            topics={topics}
            preferences={notificationPreferences}
            setPreferences={setNotificationPreferences}
            groupByReferenceType
          />
        </CustomTabPanel>

        <CustomTabPanel value={value} index={2}>
          <Alert severity='info' sx={{ mb: 2 }}>
            Diese Signatur wird verwendet, wenn die Gruppe als Absender auftritt
            — also bei E-Mails aus Vorgängen, in denen diese Mitarbeitergruppe
            hinterlegt ist und als Absender „Zugeordneter Mitarbeiter des
            Vorgangs" eingestellt ist.
          </Alert>
          <CustomEditor
            idTextModuleCategory={1}
            dictonaryIdsArray={[]}
            txt={userGroupObject.Signature ?? ''}
            setTxt={(txt: string) =>
              setUserGroupObject({ ...userGroupObject, Signature: txt })
            }
          />
        </CustomTabPanel>

        {/* Zentrales Protokoll (#1330) -- hier stand bisher gar nichts. */}
        <CustomTabPanel value={value} index={3}>
          {idUserGroup > 0 && (
            <ActivityLog
              entityType='UserGroup'
              entityId={idUserGroup}
              areas={GROUP_LOG_AREAS}
              enabled={value === 3}
            />
          )}
        </CustomTabPanel>

        <Box sx={{ mt: 10 }}>
          <Button
            sx={{ float: 'right', ml: 2 }}
            variant='contained'
            onClick={handleSave}
          >
            Speichern
          </Button>
          <Button
            sx={{ float: 'right' }}
            variant='outlined'
            onClick={() => setIdUserGroup(null)}
          >
            Abbruch
          </Button>
        </Box>
      </Box>
    );
  }
};
